import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosRequestConfig } from 'axios';

import { GO_API_URL, NODE_API_URL } from '@/const';
import client from '@/lib/http/client';
import {
	IGetCategoriesCreatorsResponse,
	IGetCategoriesResponse,
	IGetCategoryEntitiesResponse,
	IGetCategoryResponse,
	IUseGetCategoriesProps,
	IUseGetCreatorsProps,
} from '@/ts';

interface IOptions {
	page?: number;
	limit?: number;
	search?: string;
}

interface IContentMonitorOptions extends IOptions {
	sortBy?: 'created_at' | 'title';
	sortOrder?: 'asc' | 'desc';
}

export interface IQACollectionOptions extends IContentMonitorOptions {
	isUserOnly?: boolean;
	favourites?: boolean;
}

export interface CopilotHistoryResponse {
	data: any;
}

export interface ContentMonitorItem {
	id: number;
	// url: string;
	// platform: string;
	title: string;
	description: string;
	// thumbnail: string;
	// status: number;
	active: boolean;

	created_at: Date;
	items_amount: number;
	// creatorUrls: string[];
	// creatorName: string | null;
}

export interface QACollectionItem {
	id: number;
	// url: string;
	// platform: string;
	title: string;
	description: string;
	// thumbnail: string;
	// status: number;
	favourite: boolean;

	created_at: Date;
	updated_at: Date;

	prompt_count: number;
	// filter_data: string;
	// creatorUrls: string[];
	// creatorName: string | null;
}

export function prepareParams(options: any) {
	const { search, filters, ...rest } = options;

	if (filters && filters?.length > 0) {
		return {
			...rest,
			platforms: filters,
			search,
		};
	}

	return {
		...rest,
		search,
	};
}

function getContentMonitors({
	page = 1,
	limit = 10,
	sortBy,
	sortOrder,
	search = '',
}: IContentMonitorOptions) {
	const queryParams = `page=${page}&limit=${limit}&sortOrder=${sortOrder}&sortBy=${sortBy}&search=${search}`;
	// /copilot/history
	return client.get<CopilotHistoryResponse>(`/monitors/ui?${queryParams}`);
}

export function useGetContentMonitors(
	options: IContentMonitorOptions,
	isRefreshEnabled: boolean = false,
) {
	const { page, limit, sortBy, sortOrder, search } = options;
	return useQuery(
		['monitor-collections', page, limit, sortBy, sortOrder, search],
		() =>
			getContentMonitors({
				page,
				limit,
				sortBy,
				sortOrder,
				search,
			}),
		{
			keepPreviousData: false,
			refetchOnWindowFocus: false,
			staleTime: 1000 * 60 * 5,
			// refetchInterval: isRefreshEnabled ? 1000 * 10 : false,
		},
	);
}

function getQACollections({
	page = 1,
	limit = 10,
	sortBy,
	sortOrder,
	search = '',
	isUserOnly = false,
	favourites = false,
}: IQACollectionOptions) {
	const queryParams = `page=${page}&limit=${limit}&sortOrder=${sortOrder}&sortBy=${sortBy}&search=${search}&onlyUser=${isUserOnly}&favourites=${favourites}`;
	// /copilot/history
	return client.get<CopilotHistoryResponse>(
		`/copilot/qa-collections?${queryParams}`,
	);
}

export function useGetQACollections(
	options: IQACollectionOptions,
	isRefreshEnabled: boolean = false,
) {
	const { page, limit, sortBy, sortOrder, search, isUserOnly, favourites } =
		options;
	return useQuery(
		[
			'qa-collections',
			page,
			limit,
			sortBy,
			sortOrder,
			search,
			isUserOnly,
			favourites,
		],
		() =>
			getQACollections({
				page,
				limit,
				sortBy,
				sortOrder,
				search,
				isUserOnly,
				favourites,
			}),
		{
			keepPreviousData: false,
			refetchOnWindowFocus: false,
			staleTime: 1000 * 60 * 5,
			// refetchInterval: isRefreshEnabled ? 1000 * 10 : false,
		},
	);
}

export function useGetSingleMonitorById({ id, enabled = false }: any) {
	return useQuery(
		['single-monitor-collection', id],
		() => getSingleMonitorById(id),
		{
			keepPreviousData: false,
			refetchOnWindowFocus: false,
			retry: 3,
			retryDelay: 3000,
			enabled,
		},
	);
}

function getSingleMonitorById(id: number) {
	return client.get<any>(`/monitors/ui/single/${id}?`);
}

export function useGetSingleMonitorCategoriesById({
	id,
	page,
	limit,
	enabled = false,
	search,
}: any) {
	return useQuery(
		['single-monitor-categories', id, page, limit, search],
		() => getSingleMonitorCategoriesById(id, { page, limit, search }),
		{
			keepPreviousData: false,
			refetchOnWindowFocus: false,
			retry: 3,
			retryDelay: 3000,
			enabled,
		},
	);
}

function getSingleMonitorCategoriesById(id: number, options: any) {
	const params = prepareParams(options);

	return client.get<any>(`/monitors/ui/single-categories/${id}?`, {
		params,
	} as AxiosRequestConfig);
}

export function useGetSingleMonitorList({
	id,
	list_id,
	page,
	limit,
	sort,
	enabled = false,
	search,
}: any) {
	return useQuery(
		['single-monitor-list', id, list_id, page, limit, search, sort],
		() => getSingleMonitorList(id, list_id, { page, limit, search, sort }),
		{
			keepPreviousData: false,
			refetchOnWindowFocus: false,
			retry: 3,
			retryDelay: 3000,
			enabled,
		},
	);
}

function getSingleMonitorList(id: number, list_id: number, options: any) {
	const { page, limit, sort, search } = options;
	return client.get<any>(
		`/monitors/ui/${id}/single-list/${list_id}?page=${page}&limit=${limit}&sortOrder=${sort}&search=${search}`,
	);
}

export function useGetMonitorCategories({
	page,
	limit,
	enabled = false,
	search,
}: any) {
	return useQuery(
		['single-monitor-categories', page, limit, search],
		() => getMonitorCategories({ page, limit, search }),
		{
			keepPreviousData: false,
			refetchOnWindowFocus: false,
			retry: 3,
			retryDelay: 3000,
			enabled,
		},
	);
}

function getMonitorCategories(options: any) {
	const params = prepareParams(options);

	return client.get<any>(`/monitors/ui/categories?`, {
		params,
	} as AxiosRequestConfig);
}

export function useAddMonitor() {
	const queryClient = useQueryClient();

	return useMutation(addMonitor, {
		onSuccess: () => {
			queryClient.invalidateQueries(['monitor-collections']);
			queryClient.invalidateQueries(['single-monitor-collection']);
		},
	});
}

function addMonitor(data: any) {
	return client.post<any>(`/monitors/ui`, {
		...data,
	} as AxiosRequestConfig);
}

export function useDeleteSingleMonitor() {
	const queryClient = useQueryClient();

	return useMutation(deleteSingleMonitor, {
		onSuccess: () => {
			queryClient.invalidateQueries(['monitor-collections']);
		},
	});
}

function deleteSingleMonitor(monitor_id: number) {
	return client.delete<any>(`/monitors/ui/${monitor_id}`);
}

export function useDeleteMonitorCategory(monitor_id: number) {
	const queryClient = useQueryClient();

	return useMutation(deleteMonitorCategory, {
		onSuccess: () => {
			queryClient.invalidateQueries(['single-monitor-categories', monitor_id]);
		},
	});
}

function deleteMonitorCategory({
	monitor_id,
	category_id,
}: {
	monitor_id: number;
	category_id: number;
}) {
	return client.delete<any>(
		`/monitors/ui/categories/${monitor_id}/${category_id}`,
	);
}

function updateMonitor(data: any) {
	return client.put<any>(`/monitors/ui/${data?.id}`, {
		...data,
	} as AxiosRequestConfig);
}

export function useUpdateMonitor() {
	const queryClient = useQueryClient();

	return useMutation(updateMonitor, {
		onSuccess: () => {
			queryClient.invalidateQueries(['monitor-collections']);
			queryClient.invalidateQueries(['single-monitor-collection']);
		},
	});
}

export function useGetSingleMonitorCollectionById({
	id,
	enabled = false,
}: any) {
	return useQuery(
		['single-monitor-collection', id],
		() => getSingleMonitorCollectionById(id),
		{
			keepPreviousData: false,
			refetchOnWindowFocus: false,
			retryDelay: 3000,
			enabled,
		},
	);
}

function getSingleMonitorCollectionById(id: number) {
	return client.get<any>(`/monitors/ui/single/${id}?`);
}

export function useGetSingleQACollectionById({ id, enabled = false }: any) {
	return useQuery(
		['single-qa-collection', id],
		() => getSingleQACollectionById(id),
		{
			keepPreviousData: false,
			refetchOnWindowFocus: false,
			retryDelay: 3000,
			enabled,
		},
	);
}

function getSingleQACollectionById(id: number) {
	return client.get<any>(`/copilot/qa-collections/${id}?`);
}

export function useAddCreatorToMonitor() {
	const queryClient = useQueryClient();

	return useMutation(addCreatorToMonitor, {
		onSuccess: () => {
			queryClient.invalidateQueries([
				'single-monitor-categories',
				'monitor-collections',
				'single-monitor-collection',
			]);
		},
	});
}

function addCreatorToMonitor(data: any) {
	return client.post<any>(`/monitors/ui/single-creators/${data?.id}`, {
		...data,
	} as AxiosRequestConfig);
}

export function useAddCategoryToMonitor(id: number) {
	const queryClient = useQueryClient();

	return useMutation(addCategoryToMonitor, {
		onSuccess: () => {
			queryClient.invalidateQueries(['single-monitor-categories', id]);
			queryClient.invalidateQueries(['single-monitor-collection']);
		},
	});
}

function addCategoryToMonitor(data: any) {
	return client.post<any>(`/monitors/ui/categories/${data?.id}`, {
		...data,
	} as AxiosRequestConfig);
}

export function useGetCreatorByUrl(url: string) {
	return useQuery(url ? ['creator', url] : [], () => getCreatorByUrl(url), {
		// keepPreviousData: true,
		// staleTime: 1000 * 60 * 5,
		// cacheTime: 1000 * 60 * 10,
		// refetchOnWindowFocus: false,
		// refetchOnMount: false,
		retry: 1,
		retryDelay: 3000,
		enabled: (url ?? '')?.trim()?.length > 0,
	});
}

function getCreatorByUrl(url: string) {
	console.log('fetching creator by url:', url);

	return axios.get<any>(
		`https://dev.socialvoice.ai/api-nd/v1/creators/url?q=${url}`,
	);
}

export function useGetCreatorsTags({ enabled = false }: any) {
	return useQuery(['creators-tags'], () => getCreatorsTags(), {
		keepPreviousData: false,
		refetchOnWindowFocus: false,
		retry: 3,
		retryDelay: 5000,
		enabled,
	});
}

function getCreatorsTags() {
	// const params = prepareParams(options);

	return client.get<any>(`/collections/creators-tags-ui`);
}

export function useDeleteVideoFromCollection(collection_id?: number) {
	const queryClient = useQueryClient();

	return useMutation(deleteVideoFromCollection, {
		onSuccess: () => {
			queryClient.invalidateQueries(['videos-collections-with-ids']);
			queryClient.invalidateQueries(['single-videos-collections']);
			queryClient.invalidateQueries(['videos-collections']);
		},
	});
}

function deleteVideoFromCollection(data: any) {
	const { collection_id, post_id } = data;

	return client.delete<any>(
		`/collections/videos-ui/${collection_id}/${post_id}`,
	);
}

export function useAddCollection(type: 'videos' | 'creators') {
	const queryClient = useQueryClient();

	return useMutation(addCollection, {
		onSuccess: () => {
			queryClient.invalidateQueries([
				type === 'videos' ? 'videos-collections' : 'creators-collections',
			]);
		},
	});
}

function addCollection({
	type,
	title,
	description = '',
}: {
	type: 'videos' | 'creators';
	title: string;
	description?: string;
}) {
	return client.post<any>(`/collections/${type}-ui`, {
		type,
		title,
		description,
	} as AxiosRequestConfig);
}

export function useAddCreatorToCreatorsCollection(collection_id?: number) {
	const queryClient = useQueryClient();

	return useMutation(addCreatorToCreatorsCollection, {
		onSuccess: () => {
			queryClient.invalidateQueries(['single-creators-collections']);
			queryClient.invalidateQueries(['creators-collections-with-ids']);
			queryClient.invalidateQueries(['creators-collections']);
		},
	});
}

function addCreatorToCreatorsCollection(data: any) {
	const { collection_id, ...restData } = data;
	return client.post<any>(`/collections/creators-ui/${collection_id}`, {
		...restData,
	} as AxiosRequestConfig);
}

export function useDeleteCreatorFromCollection(collection_id?: number) {
	const queryClient = useQueryClient();

	return useMutation(deleteCreatorFromCollection, {
		onSuccess: () => {
			queryClient.invalidateQueries(['single-creators-collections']);
			queryClient.invalidateQueries(['creators-collections-with-ids']);
			queryClient.invalidateQueries(['creators-collections']);
		},
	});
}

function deleteCreatorFromCollection({
	collection_id,
	id,
}: {
	collection_id: number;
	id: number;
}) {
	return client.delete<any>(`/collections/creators-ui/${collection_id}/${id}`);
}

export function useCreateCreatorsCollectionAndAddCreatorIntoIt() {
	const queryClient = useQueryClient();

	return useMutation(createCreatorsCollectionAndAddCreatorIntoIt, {
		onSuccess: () => {
			queryClient.invalidateQueries(['creators-collections-with-ids']);
			queryClient.invalidateQueries(['creators-collections']);
		},
	});
}

function createCreatorsCollectionAndAddCreatorIntoIt(data: any) {
	return client.post<any>(`/collections/creators-ui/with_creator`, {
		...data,
	} as AxiosRequestConfig);
}

export function useGetCreatorsCollectionsWithIds({
	pagination,
	sortBy,
	sortOrder,
	enabled = false,
	search = '',
}: any) {
	const { page = 1, limit = 3 } = pagination;

	return useQuery(
		['creators-collections-with-ids', page, limit, sortBy, sortOrder, search],
		() =>
			getCreatorsCollectionsWithIds({
				page,
				limit,
				sortBy,
				sortOrder,
				search,
			}),
		{
			keepPreviousData: false,
			refetchOnWindowFocus: false,
			retry: 3,
			retryDelay: 5000,
			enabled,
		},
	);
}

function getCreatorsCollectionsWithIds(options: any) {
	const params = prepareParams(options);

	return client.get<any>(`/collections/creators-ui/with_creators_ids?`, {
		params,
	} as AxiosRequestConfig);
}

export function useCreateCollectionAndAddVideoIntoIt() {
	const queryClient = useQueryClient();

	return useMutation(createCollectionAndAddVideoIntoIt, {
		onSuccess: () => {
			queryClient.invalidateQueries(['videos-collections-with-ids']);
			queryClient.invalidateQueries(['videos-collections']);
		},
	});
}

function createCollectionAndAddVideoIntoIt(data: any) {
	return client.post<any>(`/collections/videos-ui/with_post_id`, {
		...data,
	} as AxiosRequestConfig);
}

export function useGetVideosCollectionsWithIds({
	pagination,
	sortBy,
	sortOrder,
	enabled = false,
	search = '',
}: any) {
	const { page = 1, limit = 3 } = pagination;

	return useQuery(
		['videos-collections-with-ids', page, limit, sortBy, sortOrder, search],
		() =>
			getVideosCollectionsWithIds({
				page,
				limit,
				sortBy,
				sortOrder,
				search,
			}),
		{
			keepPreviousData: false,
			refetchOnWindowFocus: false,
			retry: 3,
			retryDelay: 5000,
			enabled,
		},
	);
}

function getVideosCollectionsWithIds(options: any) {
	const params = prepareParams(options);

	return client.get<any>(`/collections/videos-ui/with_video_ids?`, {
		params,
	} as AxiosRequestConfig);
}

export function useDeleteSingleCollection(type: 'videos' | 'creators') {
	const queryClient = useQueryClient();

	return useMutation(deleteSingleCollection, {
		onSuccess: () => {
			queryClient.invalidateQueries([
				type === 'videos' ? 'videos-collections' : 'creators-collections',
			]);
		},
	});
}

function deleteSingleCollection({
	id,
	type,
}: {
	id: number;
	type: 'videos' | 'creators';
}) {
	return client.delete<any>(`/collections/${type}-ui/${id}`);
}

export function useGetSingleCreatorsCollectionById({
	id,
	pagination,
	sort,
	sortOrder,
	enabled = false,
	search = '',
}: any) {
	const { page = 1, limit = 20 } = pagination;

	return useQuery(
		['single-creators-collections', id, page, limit, sort, sortOrder, search],
		() =>
			getSingleCreatorsCollectionById(id, {
				page,
				limit,
				sort,
				sortOrder,
				search,
			}),
		{
			keepPreviousData: false,
			refetchOnWindowFocus: false,
			retry: 1,
			retryDelay: 3000,
			enabled,
		},
	);
}

function getSingleCreatorsCollectionById(id: number, options: any) {
	const params = prepareParams(options);

	return client.get<any>(`/collections/creators-ui/${id}?`, {
		params,
	} as AxiosRequestConfig);
}

export function useAddVideoToCollection() {
	const queryClient = useQueryClient();

	return useMutation(addVideoToCollection, {
		onSuccess: () => {
			queryClient.invalidateQueries(['videos-collections-with-ids']);
			queryClient.invalidateQueries(['videos-collections']);
		},
	});
}

function addVideoToCollection(data: any) {
	const { collection_id, ...restData } = data;

	return client.post<any>(`/collections/videos-ui/${collection_id}`, {
		...restData,
	} as AxiosRequestConfig);
}

export function useGetSingleVideosCollectionById({
	id,
	pagination,
	sort,
	sortOrder,
	enabled = false,
	search = '',
}: any) {
	const { page = 1, limit = 20 } = pagination;

	return useQuery(
		['single-videos-collections', id, page, limit, sort, sortOrder, search],
		() =>
			getSingleVideosCollectionById(id, {
				page,
				limit,
				sort,
				sortOrder,
				search,
			}),
		{
			keepPreviousData: false,
			refetchOnWindowFocus: false,
			retry: 1,
			retryDelay: 3000,
			enabled,
		},
	);
}

function getSingleVideosCollectionById(id: number, options: any) {
	const params = prepareParams(options);

	return client.get<any>(`/collections/videos-ui/${id}?`, {
		params,
	} as AxiosRequestConfig);
}

export function useGetVideosCollections({
	pagination,
	sortBy,
	sortOrder,
	enabled = false,
	search,
}: any) {
	const { page = 1, limit = 20 } = pagination;

	return useQuery(
		['videos-collections', page, limit, sortBy, sortOrder, search],
		() =>
			getVideosCollections({
				page,
				limit,
				sortBy,
				sortOrder,
				search,
			}),
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			retry: 3,
			retryDelay: 5000,
			enabled,
			staleTime: 1000 * 60 * 5,
		},
	);
}

function getVideosCollections(options: any) {
	const params = prepareParams(options);

	return client.get<any>(`/collections/videos-ui?`, {
		params,
	} as AxiosRequestConfig);
}

export function useUpdateCollection(type: 'videos' | 'creators') {
	const queryClient = useQueryClient();

	return useMutation(updateCollection, {
		onSuccess: () => {
			queryClient.invalidateQueries([
				type === 'videos' ? 'videos-collections' : 'creators-collections',
			]);
			queryClient.invalidateQueries(
				type === 'videos'
					? ['single-videos-collections']
					: ['single-creators-collections'],
			);
		},
	});
}

function updateCollection({
	type,
	id,
	title,
	description,
}: {
	type: 'videos' | 'creators';
	id: number;
	title: string;
	description?: string;
}) {
	return client.put<any>(`/collections/${type}-ui/${id}`, {
		title,
		description,
	} as AxiosRequestConfig);
}

export function useUpdateCreatorInCollection(collection_id: number) {
	const queryClient = useQueryClient();

	return useMutation(updateCreatorInCollection, {
		onSuccess: () => {
			queryClient.invalidateQueries([
				'single-creators-collections',
				collection_id,
			]);
			queryClient.invalidateQueries(['creators-tags']);
		},
	});
}

function updateCreatorInCollection(data: any) {
	return client.put<any>(
		`/collections/creators-ui/${data?.collection_id}/${data?.creator_id}`,
		{
			tags: data?.tags,
		} as AxiosRequestConfig,
	);
}

export function useGetCreatorsCollections({
	pagination,
	sort,
	sortOrder,
	search = '',
	enabled = false,
}: any) {
	const { page = 1, limit = 20 } = pagination;

	return useQuery(
		['creators-collections', page, limit, sort, sortOrder, search],
		() =>
			getCreatorsCollections({
				page,
				limit,
				sort,
				sortOrder,
				search,
			}),
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			retry: 3,
			retryDelay: 5000,
			enabled,
			staleTime: 1000 * 60 * 5,
		},
	);
}

function getCreatorsCollections(options: any) {
	const params = prepareParams(options);

	return client.get<any>(`/collections/creators-ui?`, {
		params,
	} as AxiosRequestConfig);
}

export function useGetCategories({
	pagination,
	sortBy,
	sortOrder,
	search,
}: IUseGetCategoriesProps) {
	const { page = 1, limit = 20 } = pagination;

	return useQuery(
		['categories', page, limit, sortBy, sortOrder, search],
		() =>
			getAllCategories({ page, limit, sortBy, sortOrder, search } as IOptions),
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			staleTime: 1000 * 60 * 5,
		},
	);
}

function getAllCategories(options: IOptions) {
	const params = prepareParams(options);

	return client.get<IGetCategoriesResponse>('/categories', {
		params,
	} as AxiosRequestConfig);
}

export function useGetSingleCategory(id: string) {
	return useQuery(['category', id], () => getSingleCategory(id), {
		keepPreviousData: true,
		refetchOnWindowFocus: false,
		staleTime: 1000 * 60 * 5,
	});
}

function getSingleCategory(id: string) {
	return client.get<IGetCategoryResponse>(`/categories/${id}`);
}

export function useGetSingleCategoryTopEntities(id: string) {
	return useQuery(
		['category-entities', id],
		() => getSingleCategoryTopEntities(id),
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			staleTime: 1000 * 60 * 5,
		},
	);
}

function getSingleCategoryTopEntities(id: string) {
	return client.get<IGetCategoryEntitiesResponse>(`/categories/${id}/entities`);
}

export function useGetAllCreators({
	pagination,
	sortBy,
	sortOrder,
	search,
	filters,
}: IUseGetCreatorsProps) {
	const { page = 1, limit = 20 } = pagination;

	return useQuery(
		['creators', page, limit, sortBy, sortOrder, filters, search],
		() =>
			getAllCreators({
				page,
				limit,
				sortBy,
				sortOrder,
				search,
				filters,
			} as IOptions),
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			staleTime: 1000 * 60 * 5,
		},
	);
}

function getAllCreators(options: IOptions) {
	const params = prepareParams(options);

	return client.get<IGetCategoriesCreatorsResponse>(`/creators`, {
		params,
	} as AxiosRequestConfig);
}

function getCreatorsByCategoryId(id: string, options: IOptions) {
	const params = prepareParams(options);

	return client.get<IGetCategoriesCreatorsResponse>(
		`/creators/byCategoryId/${id}`,
		{
			params,
		} as AxiosRequestConfig,
	);
}

export function useGetCreatorsByCategoryId(
	id: string,
	{ pagination, sortBy, sortOrder, search, filters }: IUseGetCategoriesProps,
	enabled = false,
) {
	const { page = 1, limit = 20 } = pagination;

	return useQuery(
		[`creators/category${id}`, page, limit, sortBy, filters, sortOrder, search],
		() =>
			getCreatorsByCategoryId(id, {
				page,
				limit,
				sortBy,
				sortOrder,
				search,
				filters,
			} as IOptions),
		{
			keepPreviousData: true,
			staleTime: 1000 * 60 * 5,
			refetchOnWindowFocus: false,
			retry: 3,
			refetchInterval: enabled ? 10 * 1000 : false,
		},
	);
}
